import { recordEducationJourneyAtom } from '@/atoms/modalJourneyObservation';
import { type CopySettings } from '@/features/copySettings/useCopySettings.types';
import { type CopyFormattingLoss } from '@heidi/editor/core';
import { ModalId } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';

export type CopyHintRequest = {
  owner: symbol;
  sessionId: string;
  formattingLoss?: CopyFormattingLoss;
  copySettings?: Readonly<CopySettings>;
  discovery: boolean;
  displayed: boolean;
};

/** One owner across all mounted V1/V2 toolbars. Never stores clinical content. */
export const pendingCopyHintAtom = atom<CopyHintRequest | null>(null);
/** Best-effort in-memory suppression for the 64 most recently displayed sessions. */
export const copyLossShownSessionIdsAtom = atom<ReadonlySet<string>>(new Set<string>());
const COPY_LOSS_SESSION_CAP = 64;

export const hasCopyFormattingLoss = (loss?: CopyFormattingLoss): boolean =>
  loss != null && Object.values(loss).some(Boolean);

/**
 * Consume the clinical-session loss guard only once lazy content actually
 * mounts, and confirm a displayed discovery as this page load's R5 journey.
 */
export const copyHintDisplayedAtom = atom(null, (get, set, owner: symbol) => {
  const pending = get(pendingCopyHintAtom);
  if (!pending || pending.owner !== owner || pending.displayed) return;
  if (hasCopyFormattingLoss(pending.formattingLoss)) {
    set(copyLossShownSessionIdsAtom, (previous: ReadonlySet<string>) => {
      const recent = [...previous].filter((id) => id !== pending.sessionId);
      return new Set([...recent, pending.sessionId].slice(-COPY_LOSS_SESSION_CAP));
    });
  }
  // R5 display confirmation on the popover's confirmed render (`CopyHint`'s
  // `onDisplayed`, in the UI PR).
  // A combined request shows discovery inside the T0 loss popover, which never
  // closes through the coachmark's queue entry, so confirm here whatever the
  // flags. Tier is decided by `recordEducationJourneyAtom` alone.
  if (pending.discovery) {
    set(recordEducationJourneyAtom, ModalId.ACTION_COPY_SETTINGS_COACHMARK);
  }
  set(pendingCopyHintAtom, { ...pending, displayed: true });
});

export function isCopyHintAnchorVisible(anchor: HTMLElement | null): boolean {
  if (!anchor?.isConnected || anchor.getClientRects().length === 0) return false;
  const rect = anchor.getBoundingClientRect();
  return (
    rect.width > 0 && rect.height > 0 && getComputedStyle(anchor).visibility !== 'hidden'
  );
}
